import api from '../../../API';
import { queryClient } from '../../../providers/AuthContext';
import { extractData, fetchApiQuery } from '../../../utils/apiResponse';

/**
 * Loader for case builder page
 * Handles both create and edit modes based on :name parameter
 * Uses React Query to prefetch and cache data
 */
export async function CaseBuilderLoader({params}){
    const networkId = params.name; // Optional parameter for editing existing case

    // Prefetch all data using React Query
    const [caseData, stainData, blockData, regionData, schemaData, metadataOptions] = await Promise.all([
        // Only fetch case data if editing (networkId provided); soft-fail so UI can show load error
        networkId ? fetchApiQuery(queryClient, {
            queryKey: ['bdsa-case', networkId],
            queryFn: () => api.get('bdsa/case', {query: {networkIdentifier: networkId}}),
            staleTime: 5 * 60 * 1000,
        }).catch(() => null) : Promise.resolve(null),
        fetchApiQuery(queryClient, {
            queryKey: ['bdsa-stain'],
            queryFn: () => api.get('bdsa/stain'),
            staleTime: 5 * 60 * 1000,
        }).catch(() => null),
        fetchApiQuery(queryClient, {
            queryKey: ['bdsa-block'],
            queryFn: () => api.get('/bdsa/block', { query: { includeRegions: true } }),
            staleTime: 5 * 60 * 1000,
        }).catch(() => null),
        fetchApiQuery(queryClient, {
            queryKey: ['bdsa-region'],
            queryFn: () => api.get('bdsa/region/template'),
            staleTime: 5 * 60 * 1000,
        }).catch(() => null),
        fetchApiQuery(queryClient, {
            queryKey: ['bdsa-schema', 'clinical'],
            queryFn: () => api.get('bdsa/schema', {query: {name: 'clinical'}}),
            staleTime: 5 * 60 * 1000,
        }).catch(() => null),
        // Soft-fail if endpoint doesn't exist yet
        fetchApiQuery(queryClient, {
            queryKey: ['bdsa-metadata-options'],
            queryFn: () => api.get('bdsa/metadata/options'),
            staleTime: 5 * 60 * 1000,
        }).catch(() => null),
    ]);

    const caseDataExtracted = extractData(caseData);
    const metadataOptionsExtracted = extractData(metadataOptions);

    return {
        ok: true,
        data: caseDataExtracted,
        stainSchema: extractData(stainData),
        blockSchema: extractData(blockData),
        regionSchema: extractData(regionData),
        schemas: extractData(schemaData),
        metadataOptions: metadataOptionsExtracted || {
            regions: extractData(blockData) || [],
            stains: extractData(stainData) || []
        },
        error: networkId && !caseDataExtracted ? 'Failed to load case' : null
    };
}
